import * as React from "react";

import { type VariantProps, cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@utils/ui";

import { Button } from "./button";

/**
 * Dismissible announcement banner — icon + message + optional action + close.
 * Composable (Banner / BannerIcon / BannerTitle / BannerAction / BannerClose),
 * modelled on shadcn.io's Banner but themed with the app's semantic tokens.
 *
 * Supports controlled (`open` + `onClose`) and uncontrolled (`defaultOpen`)
 * dismissal, and an `inset` prop for a rounded, contained card vs a full-bleed
 * edge-to-edge bar.
 */
const bannerVariants = cva("flex w-full items-center gap-3 px-4 py-3 text-sm", {
  variants: {
    variant: {
      default: "bg-background-raised text-foreground",
      brand: "bg-primary-50 text-primary-700",
      success: "bg-success-50 text-success-700",
      warning: "bg-warning-50 text-warning-700",
      destructive: "bg-destructive-50 text-destructive-700",
    },
    inset: {
      true: "rounded-lg border border-border",
      false: "rounded-none",
    },
  },
  defaultVariants: {
    variant: "default",
    inset: false,
  },
});

type BannerContextValue = { variant: BannerVariant };
const BannerContext = React.createContext<BannerContextValue>({
  variant: "default",
});

type BannerVariant = NonNullable<VariantProps<typeof bannerVariants>["variant"]>;

type BannerProps = Omit<React.ComponentProps<"div">, "onClose"> &
  VariantProps<typeof bannerVariants> & {
    /** Uncontrolled initial visibility. Defaults to visible. */
    defaultOpen?: boolean;
    /** Controlled visibility. Provide alongside `onClose`. */
    open?: boolean;
    /** Fired when the banner is dismissed (via BannerClose). */
    onClose?: () => void;
  };

function Banner({
  className,
  variant = "default",
  inset,
  defaultOpen = true,
  open,
  onClose,
  children,
  ...props
}: BannerProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = open !== undefined;
  const visible = isControlled ? open : internalOpen;

  const handleClose = React.useCallback(() => {
    if (!isControlled) setInternalOpen(false);
    onClose?.();
  }, [isControlled, onClose]);

  if (!visible) return null;

  return (
    <BannerContext.Provider value={{ variant: variant ?? "default" }}>
      <div
        data-slot="banner"
        role="status"
        className={cn(bannerVariants({ variant, inset }), className)}
        {...props}
      >
        <BannerCloseContext.Provider value={handleClose}>
          {children}
        </BannerCloseContext.Provider>
      </div>
    </BannerContext.Provider>
  );
}

// The close handler is shared down to BannerClose so consumers don't have to
// thread it manually through the composition.
const BannerCloseContext = React.createContext<(() => void) | null>(null);

/** Leading icon, tinted to the banner variant. Pass a lucide icon as a child. */
function BannerIcon({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="banner-icon"
      className={cn("flex shrink-0 items-center [&>svg]:size-5", className)}
      aria-hidden="true"
      {...props}
    />
  );
}

/** The banner message. Fills the available width so the action/close sit right. */
function BannerTitle({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="banner-title"
      className={cn("flex-1 font-medium", className)}
      {...props}
    />
  );
}

/** Trailing call-to-action. Wraps the shared Button; sized compact by default. */
function BannerAction({
  className,
  variant = "outline",
  size = "sm",
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="banner-action"
      variant={variant}
      size={size}
      className={cn("shrink-0", className)}
      {...props}
    />
  );
}

/** Dismiss (×). Calls the banner's close handler; no props needed. */
function BannerClose({
  className,
  onClick,
  ...props
}: React.ComponentProps<"button">) {
  const close = React.useContext(BannerCloseContext);
  return (
    <button
      type="button"
      data-slot="banner-close"
      aria-label="Dismiss"
      onClick={(event) => {
        onClick?.(event);
        close?.();
      }}
      className={cn(
        "grid size-6 shrink-0 place-items-center rounded-md text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      )}
      {...props}
    >
      <X className="size-4" />
    </button>
  );
}

export { Banner, BannerIcon, BannerTitle, BannerAction, BannerClose };
